/**
 * Baby Utilities
 *
 * Helper functions for working with baby data from pregnancy records.
 * Extracts baby names, genders, and indices from the pregnancy data model.
 */

import type { Pregnancy } from '@/store/pregnancyStore';

export interface BabyInfo {
  index: number;
  name: string;
  gender: 'male' | 'female' | 'neutral';
}

/**
 * Extract baby information from a pregnancy record.
 * Returns an array of babies with their index, name, and gender.
 * Falls back to a single "Baby" entry if no baby data is available.
 */
export function getBabiesFromPregnancy(pregnancy: Pregnancy | null | undefined): BabyInfo[] {
  if (!pregnancy) return [{ index: 0, name: 'Baby', gender: 'neutral' }];

  // Try baby_data.babies first (structured data from birth form)
  if (pregnancy.baby_data?.babies?.length) {
    return pregnancy.baby_data.babies.map((b, i) => ({
      index: i,
      name: b.name || `Baby ${i + 1}`,
      gender: (b.gender === 'male' || b.gender === 'female' ? b.gender : 'neutral') as BabyInfo['gender'],
    }));
  }

  // Fallback to baby_names array
  if (pregnancy.baby_names && Array.isArray(pregnancy.baby_names) && pregnancy.baby_names.length > 0) {
    return pregnancy.baby_names.map((n: string | { name: string; gender?: string }, i: number) => {
      if (typeof n === 'string') {
        return { index: i, name: n || `Baby ${i + 1}`, gender: 'neutral' as const };
      }
      const gender = n.gender === 'male' || n.gender === 'female' ? n.gender : 'neutral';
      return { index: i, name: n.name || `Baby ${i + 1}`, gender: gender as 'male' | 'female' | 'neutral' };
    });
  }

  return [{ index: 0, name: 'Baby', gender: 'neutral' }];
}

/**
 * Check if the pregnancy has multiple babies.
 */
export function hasMultipleBabies(pregnancy: Pregnancy | null | undefined): boolean {
  return getBabiesFromPregnancy(pregnancy).length > 1;
}

/**
 * Get a specific baby's name by index.
 */
export function getBabyName(pregnancy: Pregnancy | null | undefined, babyIndex: number): string {
  const babies = getBabiesFromPregnancy(pregnancy);
  return babies.find(b => b.index === babyIndex)?.name || `Baby ${babyIndex + 1}`;
}
